/* ============================================================
 * a11y-collapsible-roles.js
 * Accessibility remediation - Converge audit 2026-05-22, finding #64 (Major).
 * Added 2026-07-30.
 *
 * THE PROBLEM
 * The four footer menu headings (DISCOVER US, CUSTOMER SERVICES, LEGAL,
 * COMMERCIAL) are accordion triggers on mobile and plain headings on desktop.
 * In sections/page-footer.liquid they are marked up as:
 *
 *     <div class="wt-footer__title wt-collapse__trigger" role="button"
 *          tabindex="0" aria-expanded="false" aria-controls="CollapsibleTab-...">
 *
 * At >= 900px, CollapsibleSection.disableCollapsible() in assets/base.js does:
 *
 *     this.dataset.open = "true";                          // content IS open
 *     this.trigger.setAttribute("aria-expanded", "false");  // claims collapsed
 *     this.trigger.setAttribute("tabindex", "-1");
 *
 * So on desktop the element is not focusable, yet it still carries role="button"
 * and reports aria-expanded="false" while its content is fully visible. A screen
 * reader user is told there is a collapsed button when there is neither. That is
 * worse than the audit describes - the audit only notes the redundant role.
 *
 * NOT A PROBLEM, contrary to first impressions: these fake buttons DO respond to
 * Enter and Space. base.js attaches a body-level keydown handler that clicks any
 * element with role="button". So converting them to real <button> elements is not
 * required to fix this finding.
 *
 * THE FIX
 * Mirror base.js's own 900px breakpoint. Above it, strip the button semantics so
 * the heading is announced as a heading. Below it, restore them and keep
 * aria-expanded truthful against data-open.
 *
 * Deliberately does NOT touch tabindex - base.js owns that, and fighting it would
 * produce a race.
 * ============================================================ */

(function () {
  "use strict";

  // Must match CollapsibleSection.breakpoint in assets/base.js.
  var BREAKPOINT = 900;
  var TRIGGER = ".wt-collapse__trigger";
  var STASH = "data-a11y-stashed-controls";

  var applying = false;

  function isDesktop() {
    return window.innerWidth >= BREAKPOINT;
  }

  function apply() {
    if (applying) return;
    applying = true;
    try {
      var sections = document.querySelectorAll("collapsible-section[data-mobile-only]");

      for (var i = 0; i < sections.length; i++) {
        var section = sections[i];
        var trigger = section.querySelector(TRIGGER);
        if (!trigger) continue;

        if (isDesktop()) {
          // Not interactive here: drop the button semantics entirely rather than
          // leaving a role with a false expanded state attached to it.
          if (trigger.hasAttribute("aria-controls")) {
            trigger.setAttribute(STASH, trigger.getAttribute("aria-controls"));
            trigger.removeAttribute("aria-controls");
          }
          trigger.removeAttribute("role");
          trigger.removeAttribute("aria-expanded");
        } else {
          trigger.setAttribute("role", "button");
          if (trigger.hasAttribute(STASH)) {
            trigger.setAttribute("aria-controls", trigger.getAttribute(STASH));
          }
          // Keep the announced state matching reality.
          trigger.setAttribute(
            "aria-expanded",
            section.dataset.open === "true" ? "true" : "false"
          );
        }
      }
    } catch (e) {
      if (window.console && console.warn) console.warn("a11y-collapsible-roles:", e);
    }
    applying = false;
  }

  function schedule() {
    window.setTimeout(apply, 0);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
  } else {
    schedule();
  }

  // base.js reapplies its own attributes on resize and on toggle, so re-assert
  // after it. The `applying` guard stops the observer from re-entering.
  window.addEventListener("resize", schedule);
  document.addEventListener("click", schedule, true);

  if (window.MutationObserver) {
    var observer = new MutationObserver(schedule);
    document.addEventListener("DOMContentLoaded", function () {
      var sections = document.querySelectorAll("collapsible-section[data-mobile-only]");
      for (var i = 0; i < sections.length; i++) {
        observer.observe(sections[i], {
          attributes: true,
          attributeFilter: ["data-open"],
        });
      }
    });
  }
})();
